/**
 * UI slice for the application store.
 * Handles UI state like the sidebar, loading, errors, and navigation counts.
 */

import { ATTENTION_ROUTES } from '@shared/constants/routes';

import type { StateCreator } from 'zustand';

/**
 * Navigation counts for sidebar badges.
 */
export interface NavCounts {
  emailInbox: number | null;
  starredInbox: number | null;
  readLaterInbox: number | null;
  drafts: number | null;
}

/**
 * UI state and actions.
 */
export interface UISlice {
  // Loading and errors
  isLoading: boolean;
  error: string | null;

  /** Route to restore when switching back to home mode. */
  homeRoute: string;
  setHomeRoute: (route: string) => void;

  // Sidebar state
  isSidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;

  // Navigation counts (for sidebar badges)
  navCounts: NavCounts;
  setNavCounts: (counts: Partial<NavCounts>) => void;
  setEmailInboxCount: (count: number | null) => void;
  setStarredInboxCount: (count: number | null) => void;
  setReadLaterInboxCount: (count: number | null) => void;
  setDraftsCount: (count: number | null) => void;

  // SSE-driven email refresh coordination
  emailRefreshTrigger: number;
  triggerEmailRefresh: () => void;
  /** True while AT LEAST ONE email SSE connection is live. */
  sseConnected: boolean;
  /** Ids of the connections currently reporting themselves live. */
  sseConnectionIds: string[];
  /**
   * Report one connection's state, identified by a stable per-hook id.
   *
   * Today the only reporter is the shell's app-wide `useEmailNotificationStream`.
   * Keyed rather than a plain boolean because it once had several (the inbox
   * list and the email page each opened a stream), and with a single boolean
   * ONE unmounting hook's cleanup reported "disconnected" while the others were
   * still streaming, dropping `useNavCounts` to its aggressive poll interval.
   *
   * Keyed rather than refcounted, because the reports are NOT balanced: a hook
   * reports `false` from both its stream's end and its stop, and
   * reports `true` again on every reconnect. Counting those would drift; adding
   * and removing an id is idempotent, so repeats are harmless.
   */
  setSSEConnected: (connectionId: string, connected: boolean) => void;

  // Loading/error actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

/**
 * Creates the UI slice.
 */
export const createUISlice: StateCreator<UISlice, [], [], UISlice> = set => ({
  // Initial state
  isLoading: false,
  error: null,
  homeRoute: ATTENTION_ROUTES.root,
  setHomeRoute: route => set({ homeRoute: route }),
  isSidebarCollapsed:
    typeof window !== 'undefined'
      ? (localStorage.getItem('sidebar-collapsed') ?? 'true') === 'true'
      : true,
  navCounts: {
    emailInbox: null,
    starredInbox: null,
    readLaterInbox: null,
    drafts: null,
  },

  // SSE-driven email refresh coordination
  emailRefreshTrigger: 0,
  triggerEmailRefresh: () => set(state => ({ emailRefreshTrigger: state.emailRefreshTrigger + 1 })),
  sseConnected: false,
  sseConnectionIds: [],
  setSSEConnected: (connectionId, connected) =>
    set(state => {
      const has = state.sseConnectionIds.includes(connectionId);
      if (connected === has) return state; // idempotent: nothing moved
      const ids = connected
        ? [...state.sseConnectionIds, connectionId]
        : state.sseConnectionIds.filter(id => id !== connectionId);
      return { sseConnectionIds: ids, sseConnected: ids.length > 0 };
    }),

  // Loading/error actions
  setLoading: loading => set({ isLoading: loading }),

  setError: error => set({ error }),

  // Sidebar actions
  setSidebarCollapsed: collapsed => {
    localStorage.setItem('sidebar-collapsed', String(collapsed));
    set({ isSidebarCollapsed: collapsed });
  },

  toggleSidebar: () =>
    set(state => {
      const newValue = !state.isSidebarCollapsed;
      localStorage.setItem('sidebar-collapsed', String(newValue));
      return { isSidebarCollapsed: newValue };
    }),

  // Navigation count actions
  setNavCounts: counts =>
    set(state => ({
      navCounts: { ...state.navCounts, ...counts },
    })),

  setEmailInboxCount: count =>
    set(state => ({
      navCounts: { ...state.navCounts, emailInbox: count },
    })),

  setStarredInboxCount: count =>
    set(state => ({
      navCounts: { ...state.navCounts, starredInbox: count },
    })),

  setReadLaterInboxCount: count =>
    set(state => ({
      navCounts: { ...state.navCounts, readLaterInbox: count },
    })),

  setDraftsCount: count =>
    set(state => ({
      navCounts: { ...state.navCounts, drafts: count },
    })),
});
